/**
 * THUB-290: make a caught render error identifiable and durable.
 *
 * WHY THIS EXISTS
 * A render exception used to replace the page with a bare "Content Error" and discard every
 * detail: the identity was gated behind `NODE_ENV === 'development'`, and `componentDidCatch`
 * only wrote a `console.error` that dies with the tab. An operator could report nothing beyond
 * the words on screen, and we could not tell which component threw, whether it recurred, or
 * whether two reports were the same bug.
 *
 * That cost real time on 2026-08-07: a Content Error on the NP Dashboard took a full
 * log-diving pass across five Lambdas (all clean — zero errors, zero 5xx, zero breaker trips,
 * every API call 200) and still did not reach root cause, because the only evidence had been
 * thrown away. A `window.onerror` listener does not help either: React's boundary catches
 * render errors before they reach it, which I confirmed by installing one and capturing
 * nothing.
 *
 * WHAT THIS IS NOT
 * Not a telemetry pipeline. There is no error-reporting backend today, so this deliberately
 * uses `localStorage` — durable past the tab, readable by anyone with the console, and zero
 * new infrastructure. If a real sink appears later, `recordRenderError` is the single seam to
 * point at it.
 */

/** A caught render error, reduced to what is useful and safe to show. */
export interface RenderErrorRecord {
  /** Short id an operator can read aloud or paste into a ticket. */
  correlationId: string;
  /** e.g. "TypeError" */
  name: string;
  message: string;
  /** Best guess at the component that threw, from the React component stack. */
  component: string;
  /** ISO-8601 UTC. */
  at: string;
  /** Route the user was on — an intermittent error is often route-specific. */
  path: string;
  stack: string;
  componentStack: string;
}

const STORAGE_KEY = 'transferhub.renderErrors';
/** Keep the last few. Enough to spot a repeat; small enough never to bloat localStorage. */
const MAX_RECORDS = 10;

/**
 * A short, human-quotable id. Not a UUID on purpose: an operator has to be able to read this
 * over a call or type it into a ticket, and 8 chars of base36 is plenty to correlate one
 * report against one stored record.
 */
function shortId(): string {
  const rand = Math.random().toString(36).slice(2, 6);
  const time = Date.now().toString(36).slice(-4);
  return `${time}${rand}`.toUpperCase();
}

/**
 * First meaningful frame of a React component stack.
 *
 * The raw stack is many lines of "\n    in X (at Y)"; the first entry is the component that
 * threw, which is the single most useful fact and the one the old fallback omitted.
 */
export function componentFromStack(componentStack: string | null | undefined): string {
  if (!componentStack) return 'unknown';
  for (const raw of componentStack.split('\n')) {
    const line = raw.trim();
    if (!line.startsWith('in ')) continue;
    const name = line.slice(3).split(' ')[0].trim();
    // Skip framework wrappers — they are never the interesting frame.
    if (!name || /^(ErrorBoundary|Suspense|Fragment|Unknown)$/.test(name)) continue;
    return name;
  }
  return 'unknown';
}

/** Reduce an Error + ErrorInfo to a record. Pure — safe to call in `getDerivedStateFromError`. */
export function summarizeError(
  error: Error,
  componentStack: string | null | undefined,
  path: string,
): RenderErrorRecord {
  return {
    correlationId: shortId(),
    name: error?.name || 'Error',
    message: error?.message || String(error),
    component: componentFromStack(componentStack),
    at: new Date().toISOString(),
    path,
    // Bounded: a stack is unbounded in principle and this goes into localStorage.
    stack: (error?.stack || '').slice(0, 4000),
    componentStack: (componentStack || '').slice(0, 4000),
  };
}

/** Read the stored records, newest first. Never throws — diagnostics must not break a page. */
export function getRenderErrors(): RenderErrorRecord[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Persist a record, newest first, capped at MAX_RECORDS.
 *
 * Every failure path is swallowed on purpose: localStorage can be disabled, full, or blocked
 * by policy. A diagnostics helper that throws inside an error boundary would turn a
 * recoverable render error into a blank page, which is strictly worse than losing the log.
 */
export function recordRenderError(record: RenderErrorRecord): void {
  // Always emit to the console too — it is the one sink that needs no permission, and it keeps
  // the record visible to anyone already looking at devtools.
  try {
    // eslint-disable-next-line no-console
    console.error(
      `[TransferHub render error ${record.correlationId}] ${record.name}: ${record.message} ` +
      `(component: ${record.component}, path: ${record.path})`,
    );
  } catch {
    /* console unavailable — ignore */
  }

  try {
    const next = [record, ...getRenderErrors()].slice(0, MAX_RECORDS);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable/full — the console line above is the fallback */
  }
}

export function clearRenderErrors(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Expose a console helper so support can extract records without devtools archaeology:
 *   > transferhubErrors()
 *
 * Attached at module load, guarded because this module is imported by tests in jsdom and by
 * the production bundle alike.
 */
try {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).transferhubErrors = getRenderErrors;
  }
} catch {
  /* ignore */
}
